import { trim } from 'lodash';

import { getAssetTypeMaxCopyLength } from 'src/data/asset';
import { GENDER, getAgeGroupLabel, getGenderLabel } from 'src/data/demographics';
import { GENERATION_MODE_TYPE } from 'src/data/generation';
import {
  doesToolSupportCustomerPersona,
  GENERATION_TOOL,
  GENERATION_TOOL_GROUP,
  type GenerationTool,
  getGroupByTool,
  getToolDefaultAssetType,
  getToolDescription,
  getToolTitle,
  isGenerationToolInSocialGroup,
} from 'src/data/generationTool';
import { CUSTOMER_PERSONA_EMPTY_LABEL, type CustomerPersona } from 'src/data/persona';

import type { InfinityGenerationParams } from './types';

const convertToSingleLine = (str: string): string => str.replace(/\n+/g, ' ');
const truncateSpaces = (str: string): string => str.replace(/\s+/g, ' ');

const freeTextPrefix = (generationTool: GenerationTool): string => {
  if (getGroupByTool(generationTool) === GENERATION_TOOL_GROUP.email) {
    return 'Promotion';
  }
  return 'Brief';
};

const CUSTOM_TOOL_TITLES: Partial<Record<GenerationTool, string>> = {
  [GENERATION_TOOL.emailSubjectLine]: 'Email Subject line',
  [GENERATION_TOOL.emailColdOutreachBody]: 'cold email to a prospect',
  [GENERATION_TOOL.landingHeadline]: 'Webpage Headline',
  [GENERATION_TOOL.landingSubheader]: 'Webpage Subheadline',
  [GENERATION_TOOL.landingSEOMetadata]: 'SEO Meta Description',
};

const formatTitleLine = (params: InfinityGenerationParams): string => {
  const result = [];

  const customTitle = CUSTOM_TOOL_TITLES[params.generationTool];
  if (customTitle) {
    result.push(`Write a ${customTitle}`);
  } else {
    const toolTitle = getToolTitle(params.generationTool);
    result.push(`Write a ${toolTitle}`);

    const toolDescription = getToolDescription(params.generationTool);
    if (toolDescription) {
      result.push(toolDescription);
    }
  }

  return result.join(' ');
};

const shouldSkipCountAndLimitsLines = (params: InfinityGenerationParams): boolean =>
  isGenerationToolInSocialGroup(params.generationTool) ||
  (
    [GENERATION_TOOL.emailSubjectLine, GENERATION_TOOL.emailColdOutreachBody] as GenerationTool[]
  ).includes(params.generationTool);
const formatCountAndLimitsLines = (params: InfinityGenerationParams): string => {
  if (shouldSkipCountAndLimitsLines(params)) {
    return '';
  }

  const result = [];

  const assetType = getToolDefaultAssetType(params.generationTool);
  const maxLength = assetType ? getAssetTypeMaxCopyLength(assetType) : 0;
  if (maxLength > 0) {
    result.push(`Each one up to ${maxLength} chars long.`);
  }

  return result.join('\n');
};

/*
 * Brief lines
 */
const prepareBriefContent = (str: string): string => convertToSingleLine(truncateSpaces(trim(str)));
const extractedBriefOrFreeText = (params: InfinityGenerationParams): string =>
  prepareBriefContent(params.extractedBrief || params.brief?.freeText || '');
const formatBriefLines = (params: InfinityGenerationParams): string => {
  const result = [];

  if (params.generationTool === GENERATION_TOOL.emailSubjectLine) {
    result.push(`For this email ${extractedBriefOrFreeText(params)}`);
  } else if (isGenerationToolInSocialGroup(params.generationTool)) {
    result.push(extractedBriefOrFreeText(params));
  } else {
    result.push('');
    result.push(`Write the ads using the following context:`);

    if (params.extractedBrief) {
      const prefix = freeTextPrefix(params.generationTool);
      result.push(`${prefix}: ${prepareBriefContent(params.extractedBrief)}`);
    } else if (params.brief) {
      if (params.brief.title != null) {
        result.push(`Product name: ${prepareBriefContent(params.brief.title)}`);
      }
      if (params.brief.body != null) {
        result.push(`Product description: ${prepareBriefContent(params.brief.body)}`);
      }
      if (params.brief.freeText != null) {
        const prefix = freeTextPrefix(params.generationTool);
        result.push(`${prefix}: ${prepareBriefContent(params.brief.freeText)}`);
      }
    }
  }

  return result.join('\n');
};

export const formatPersonaPromptComponent = (persona?: CustomerPersona): string => {
  const result = [];

  if (persona) {
    result.push(trim(persona.name || ''));
    if (persona.minAge && persona.maxAge) {
      result.push(`age: ${persona.minAge} to ${getAgeGroupLabel(persona.maxAge)}`);
    }
    if (persona.gender && persona.gender !== GENDER.doesntMatter) {
      result.push(`gender: ${getGenderLabel(persona.gender)}`);
    }
    if (persona.painPoints && persona.painPoints?.length > 0) {
      result.push(`persona’s pain points are: ${persona.painPoints.join(', ')}`);
    }
  } else {
    result.push(CUSTOMER_PERSONA_EMPTY_LABEL);
  }

  return result.join(', ');
};

export const formatGenerationPrompt = (params: InfinityGenerationParams): string => {
  const result = [];

  if (params.suggestionMode) {
    // Title line
    const title = formatTitleLine(params);
    result.push(title);

    const countAndLimits = formatCountAndLimitsLines(params);
    if (countAndLimits) {
      result.push('');
      result.push(countAndLimits);
    }

    // Brief
    result.push(formatBriefLines(params));

    // Customer persona
    if (doesToolSupportCustomerPersona(params.generationTool)) {
      result.push('');
      result.push(
        `Customer persona to write for: ${formatPersonaPromptComponent(params.customerPersona)}`
      );
    }

    // "personalized"/"use examples" does not use annotations
    if (params.suggestionMode !== GENERATION_MODE_TYPE.personalized) {
      // Annotations / talking points
      if (params.annotations && params.annotations?.length > 0) {
        const annotationsStr = params.annotations.map(({ content }) => content).join(', ');
        result.push(`Must use the following talking points with these ads: ${annotationsStr}`);
      }
    }
  } else {
    // "recompose"-specific formatting
  }

  return result.join('\n');
};
